/*
 * @Author: Teemo Ding
 * @Date: 2021-07-13 16:22:54
 * @LastEditors: Json Chen
 * @LastEditTime: 2022-06-13 13:21:12
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelOrder/MaximumPurchaseLimitation/index.tsx
 */
import { Checkbox, NumberInput, Radio, Select } from '@/src/components/Antd';
import Table from '@/src/components/Antd/Table/SizeFittingTable';
import { EditIcon } from '@/src/components/EditableTable/EditableTable';
import SectionWrapper from '@/src/components/Layouts/Section/SectionWrapper';
import { CaretDownOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { Button, Form } from 'antd';
import _ from 'lodash';
import React, { Fragment } from 'react';
import { useParams } from 'react-router';
import { EnumMode } from 'src/components/Antd/NumberInput/type';
import { TableNumberInput } from 'src/components/EditableTable/TableForm/TableForm';
import WhiteBackgroundTooltip, {
  TooltipContainer,
  TooltipContent,
  TooltipTitle,
} from 'src/components/GlobalTooltip/WhiteBackgroundTooltip';
import SplitLine from 'src/components/SplitLine/SplitLine';
import WarningPopReminder from 'src/constrains/PopReminder/Warning';
import CollectionProductsSelection from 'src/constrains/ProductsSelection/CollectionProductsSelection';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import {
  LimitationAppliesValues,
  LimitationPeriodValues,
  limitationApplies,
  limitationPeriodList,
  periodUnitList,
} from '../config';
import css from '../css.module.less';

const MaximumPurchaseLimitation = ({
  specificProducts,
  setSpecificProducts,
  disableEdit,
}) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const params: any = useParams();
  const [visible, setVisible] = React.useState(false);

  const deleteFun = (uuid) => {
    const specificProductsCopy = specificProducts.filter(
      (o) => o.uuid !== uuid,
    );
    setSpecificProducts(specificProductsCopy);
  };

  const onCancel = () => {
    setVisible(false);
  };

  const { uuid: salesChannelUuid } = params;

  const onOk = (products = []) => {
    const _productsWithSalesChannelProductUuid = products.map((i) => {
      return {
        ...i,
        salesChannelProductUuid: i.salesChannelProductUuid || i.uuid,
      };
    });

    const _pWithDraggableId = _productsWithSalesChannelProductUuid.map((i) => {
      const preMaxPurchaseLimitation =
        _.get(
          (specificProducts || []).find((p) => {
            return (
              _.get(p, 'salesChannelProductUuid') ===
              _.get(i, 'salesChannelProductUuid')
            );
          }),
          'maxPurchaseLimitation',
        ) ||
        _.get(i, 'maxPurchaseLimitation') ||
        1;

      return {
        ...i,
        maxPurchaseLimitation: preMaxPurchaseLimitation,
        salesChannelProductUuid: i.uuid,
        name: i.productName || '',
      };
    });

    setSpecificProducts(_pWithDraggableId);
  };

  const onOpen = () => {
    setVisible(true);
  };

  const specificProductsColumns = () => [
    {
      title: getI18n('general.storeCommunicationSetting.product'),
      dataIndex: 'productName',
      default: true,
      optional: true,
      key: 'productName',
      className: css.columnClass,
      minWidth: 236,
    },
    {
      title: () => (
        <div>
          <EditIcon />
          <span>
            {getI18n('salesChannelOrder.index.maximumPurchaseLimitation')}
          </span>
        </div>
      ),
      childGenerate: (text, record, index) => {
        const { maxPurchaseLimitation, maxPurchaseLimitationError } = record;
        return (
          <TableNumberInput
            error={maxPurchaseLimitationError}
            mode={EnumMode.int}
            disabled={disableEdit}
            min={1}
            value={maxPurchaseLimitation}
            onChange={(e) => {
              const specificProductsCopy = _.cloneDeep(specificProducts);
              specificProductsCopy[index].maxPurchaseLimitation = e;
              setSpecificProducts(specificProductsCopy);
            }}
          />
        );
      },
      key: 'maxPurchaseLimitation',
      minWidth: 144,
      default: true,
    },
    disableEdit
      ? {}
      : {
          title: getI18n(
            'general.advancedFilters.commonStoreProductsWithTable.actions',
          ),
          dataIndex: 'remarks',
          default: true,
          optional: true,
          key: 'remarks',
          className: css.columnClass,
          minWidth: 120,
          render: (text, record) => (
            <WarningPopReminder
              title={getI18n('salesChannelOrder.index.removeProduct')}
              content={getI18n('salesChannelOrder.index.removeProductContent')}
              onOkText={getI18n('general.remove')}
              onOk={() => {
                deleteFun(record.uuid);
              }}>
              <a href='#' className={css.errorText}>
                {getI18n('general.delete')}
              </a>
            </WarningPopReminder>
          ),
        },
  ];

  return (
    <SectionWrapper
      titleClassName={css.titleContainer}
      containerClassName={css.invoiceFormat}
      title={getI18n('salesChannelOrder.index.maximumPurchaseLimitation')}>
      {visible && (
        <CollectionProductsSelection
          visible={visible}
          onCancel={onCancel}
          onOk={onOk}
          selectedItems={specificProducts}
          salesChannelUuid={salesChannelUuid}
        />
      )}
      <Form.Item
        noStyle
        name='enableMaxPurchaseToAllProducts'
        valuePropName='checked'>
        <Checkbox className={css.checkboxBox} disabled={disableEdit}>
          {getI18n(
            'salesChannelOrder.index.enableMaxPurchaseToAllProductslabel',
          )}
        </Checkbox>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.enableMaxPurchaseToAllProducts !==
          currentValues.enableMaxPurchaseToAllProducts
        }>
        {({ getFieldValue }) =>
          getFieldValue('enableMaxPurchaseToAllProducts') && (
            <div
              className={`${css.checkboxChildContent} ${css.maxNumPerProduct}`}>
              <span>{getI18n('salesChannelOrder.index.maximum')}</span>
              <Form.Item
                name='maxNumPerProduct'
                className={css.maxNumPerProductInput}
                rules={[
                  {
                    required: true,
                    message: getI18n('general.required'),
                  },
                ]}>
                <NumberInput
                  size='large'
                  min={1}
                  wrapperClassName={css.inputWrapper}
                  disabled={disableEdit}
                />
              </Form.Item>
              <span>{getI18n('salesChannelOrder.index.ofeachproduct')}</span>
            </div>
          )
        }
      </Form.Item>

      <Form.Item
        noStyle
        name='enableMaxPurchaseToSpecificProducts'
        valuePropName='checked'>
        <Checkbox className={css.checkboxBox} disabled={disableEdit}>
          {getI18n(
            'salesChannelOrder.index.enableMaxPurchaseToSpecificProductsLabel',
          )}
          <WhiteBackgroundTooltip
            title={
              <TooltipContainer>
                <TooltipTitle>
                  {getI18n('salesChannelOrder.index.maximumPurchaseLimitation')}
                </TooltipTitle>
                <TooltipContent>
                  {getI18n(
                    'salesChannelOrder.index.maximumPurchaseLimitationTip',
                  )}
                </TooltipContent>
              </TooltipContainer>
            }
            placement='rightTop' //overlayClassName
          >
            <QuestionCircleFilled className={css.icon} rev />
          </WhiteBackgroundTooltip>
        </Checkbox>
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.enableMaxPurchaseToSpecificProducts !==
          currentValues.enableMaxPurchaseToSpecificProducts
        }>
        {({ getFieldValue }) =>
          getFieldValue('enableMaxPurchaseToSpecificProducts') && (
            <div
              className={`${css.specificProductsContent} ${css.checkboxChildContent}`}>
              <div className={css.specificProductsContentTitle}>
                {getI18n(
                  'salesChannelOrder.index.specificProductsContentTitle',
                )}
              </div>
              {specificProducts && specificProducts.length > 0 && (
                <Table
                  rowKey={'uuid'}
                  dataSource={specificProducts || []}
                  columns={specificProductsColumns()}
                  id='specificProducts'
                  pagination={false}
                />
              )}
              <Button
                type='primary'
                onClick={onOpen}
                disabled={disableEdit}
                className={css.specificProductsContentBtn}>
                {getI18n('general.selectProduct')}
              </Button>
            </div>
          )
        }
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.enableMaxPurchaseToAllProducts !==
            currentValues.enableMaxPurchaseToAllProducts ||
          prevValues.enableMaxPurchaseToSpecificProducts !==
            currentValues.enableMaxPurchaseToSpecificProducts
        }>
        {({ getFieldValue }) =>
          (getFieldValue('enableMaxPurchaseToAllProducts') ||
            getFieldValue('enableMaxPurchaseToSpecificProducts')) && (
            <Fragment>
              <SplitLine className={css.splitLine} />
              <Form.Item
                name='appliesTo'
                labelCol={{
                  span: 24,
                }}
                label={getI18n('salesChannelOrder.index.limitationAppliesto')}>
                <Radio
                  disabled={disableEdit}
                  className={css.informationCustomized}
                  radioList={limitationApplies({ getI18n })}></Radio>
              </Form.Item>
            </Fragment>
          )
        }
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.appliesTo !== currentValues.appliesTo ||
          prevValues.limitationPeriod != currentValues.limitationPeriod
        }>
        {({ getFieldValue }) =>
          getFieldValue('appliesTo') ==
            LimitationAppliesValues.EACH_CUSTOMER && (
            <div
              className={`${css.checkboxChildContent} ${css.limitationPeriod}`}>
              <span>{getI18n('salesChannelOrder.index.limitationPeriod')}</span>
              <Form.Item name='limitationPeriod'>
                <Select
                  disabled={disableEdit}
                  size='large'
                  optionsList={limitationPeriodList({ getI18n })}
                  suffixIcon={
                    <CaretDownOutlined className={css.selectIcon} rev />
                  }
                />
              </Form.Item>
              {getFieldValue('limitationPeriod') ==
                LimitationPeriodValues.SPECIFIC_PERIOD && (
                <div className={css.limitationPeriod}>
                  <Form.Item name='periodValue'>
                    <NumberInput
                      size='large'
                      min={1}
                      wrapperClassName={css.inputWrapper}
                      disabled={disableEdit}
                    />
                  </Form.Item>
                  <Form.Item name='periodUnit'>
                    <Select
                      size='large'
                      optionsList={periodUnitList({ getI18n })}
                      disabled={disableEdit}
                      suffixIcon={
                        <CaretDownOutlined className={css.selectIcon} rev />
                      }
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          )
        }
      </Form.Item>
    </SectionWrapper>
  );
};

export default MaximumPurchaseLimitation;
